import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Button, Badge, Spinner, Text,
  makeStyles, tokens,
} from '@fluentui/react-components'
import { Edit24Regular, ArrowLeft24Regular, ClipboardTask24Regular, CheckmarkCircle24Regular } from '@fluentui/react-icons'
import { contractorsService } from '../../services/contractorsService'
import { documentDefinitionsService, DocTypeLabels, type PersonDocumentRecord } from '../../services/documentDefinitionsService'
import { useAuth } from '../../auth/useAuth'
import AresInfoPanel from '../../components/AresInfoPanel'

const useStyles = makeStyles({
  header:      { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' },
  card:        { backgroundColor: tokens.colorNeutralBackground1, borderRadius: tokens.borderRadiusMedium, padding: '24px', boxShadow: tokens.shadow4 },
  infoGrid:    { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoGrid3:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px 32px', marginTop: '16px' },
  infoLabel:   { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.07em', color: tokens.colorNeutralForeground3, marginBottom: '2px' },
  infoValue:   { fontSize: '14px', fontWeight: 600, color: tokens.colorNeutralForeground1, lineHeight: '1.4' },
  sectionTitle: { fontSize: tokens.fontSizeBase400, fontWeight: tokens.fontWeightSemibold, paddingBottom: '10px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}` },
  subsection:  { fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.06em', color: tokens.colorNeutralForeground2, marginTop: '20px', paddingTop: '16px', borderTop: `1px solid ${tokens.colorNeutralBackground3}` },
  docTable:    { width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginTop: '12px' },
  th:          { textAlign: 'left', padding: '8px 12px', borderBottom: `2px solid ${tokens.colorNeutralStroke2}`, fontWeight: 600, fontSize: '11px', color: tokens.colorNeutralForeground3, textTransform: 'uppercase', letterSpacing: '.05em', background: tokens.colorNeutralBackground2 },
  td:          { padding: '9px 12px', borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, verticalAlign: 'middle' },
})

function InfoField({ label, value, children }: {
  label: string; value?: string | null; children?: React.ReactNode
}) {
  const styles = useStyles()
  const content = children ?? value
  if (!content) return null
  return (
    <div>
      <div className={styles.infoLabel}>{label}</div>
      <div className={styles.infoValue}>{content}</div>
    </div>
  )
}

const fmt = (d: string | null | undefined) => d ? new Date(d).toLocaleDateString('cs-CZ') : null

function deadlinePassed(d: string | null | undefined): boolean {
  if (!d) return false
  return new Date(d) < new Date()
}

function cooperationYears(startDate: string | null | undefined): number | null {
  if (!startDate) return null
  const start = new Date(startDate)
  const today = new Date()
  let years = today.getFullYear() - start.getFullYear()
  const m = today.getMonth() - start.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < start.getDate())) years--
  return years >= 0 ? years : null
}

export default function ContractorDetailPage() {
  const styles = useStyles()
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { isHR, isAdmin } = useAuth()
  const canEdit = isHR || isAdmin

  const { data, isLoading } = useQuery({
    queryKey: ['contractor', id],
    queryFn: () => contractorsService.getById(Number(id)),
    enabled: !!id,
  })

  const { data: docs = [] } = useQuery({
    queryKey: ['contractor-documents', id],
    queryFn: () => documentDefinitionsService.getContractorDocuments(Number(id)),
    enabled: !!id,
  })

  const completeMutation = useMutation({
    mutationFn: (docId: number) => documentDefinitionsService.completeDocument(docId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['contractor-documents', id] }),
  })

if (isLoading) return <Spinner label="Načítám..." />
  if (!data) return <Text>Dodavatel nenalezen.</Text>

  return (
    <div>
      <div className={styles.header}>
        <Button appearance="subtle" icon={<ArrowLeft24Regular />} onClick={() => navigate('/contractors')}>
          Zpět
        </Button>
        <Text size={600} weight="semibold">{data.companyName}</Text>
        <Badge appearance="filled" color={data.isActive ? 'success' : 'danger'}>
          {data.isActive ? 'Aktivní' : 'Neaktivní'}
        </Badge>
        {canEdit && (
          <Button appearance="primary" icon={<Edit24Regular />} onClick={() => navigate(`/contractors/${id}/edit`)}>
            Upravit
          </Button>
        )}
      </div>

      <div className={styles.card}>
        <Text className={styles.sectionTitle}>Základní informace</Text>
        <div className={styles.infoGrid} style={{ marginTop: 16 }}>
          <InfoField label="IČO"        value={data.ico} />
          <InfoField label="Plátce DPH" value={data.isVatPayer ? 'Ano' : 'Ne'} />
        </div>

        <div className={styles.subsection}>Kontaktní osoba</div>
        <div className={styles.infoGrid}>
          <InfoField label="Jméno"             value={data.contactFirstName} />
          <InfoField label="Příjmení"           value={data.contactLastName} />
          <InfoField label="Kontaktní e-mail">
            {data.contactEmail
              ? <a href={`mailto:${data.contactEmail}`} style={{ color: tokens.colorBrandForeground1 }}>{data.contactEmail}</a>
              : null}
          </InfoField>
          <InfoField label="Telefon" value={data.phone} />
          <InfoField label="Datum narození"     value={fmt(data.birthDate)} />
          <InfoField label="Zahájení spolupráce" value={fmt(data.cooperationStartDate)} />
          {(() => {
            const years = cooperationYears(data.cooperationStartDate)
            return years !== null
              ? <InfoField label="Délka spolupráce" value={`${years} ${years === 1 ? 'rok' : years <= 4 ? 'roky' : 'let'}`} />
              : null
          })()}
        </div>

        <div className={styles.subsection}>Smlouva</div>
        <div className={styles.infoGrid}>
          <InfoField label="Začátek platnosti smlouvy"         value={fmt(data.contractStartDate)} />
          <InfoField label="Konec smlouvy / ukončení spolupráce" value={fmt(data.contractEndDate) ?? 'Neurčito'} />
          {data.expectedActivities && (
            <InfoField label="Předpokládané činnosti / role" value={data.expectedActivities} />
          )}
          <InfoField label="Oddělení" value={data.departmentName} />
          <InfoField label="Odpovědný manažer" value={data.managerFullName} />
          <InfoField label="ORBIT e-mail">
            {data.orbitEmail
              ? <a href={`mailto:${data.orbitEmail}`} style={{ color: tokens.colorBrandForeground1 }}>{data.orbitEmail}</a>
              : null}
          </InfoField>
        </div>

        {data.note && (
          <>
            <div className={styles.subsection}>Poznámka</div>
            <div style={{ fontSize: 14, color: tokens.colorNeutralForeground2, fontStyle: 'italic', marginTop: 8, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
              {data.note}
            </div>
          </>
        )}
      </div>

      <AresInfoPanel ico={data.ico} />

      {/* Dokumenty */}
      {docs.length > 0 && (
        <div className={styles.card} style={{ marginTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <ClipboardTask24Regular />
            <Text className={styles.sectionTitle} style={{ borderBottom: 'none', paddingBottom: 0 }}>Dokumenty</Text>
            {docs.filter((d: PersonDocumentRecord) => !d.isCompleted).length > 0 && (
              <Badge color="danger">{docs.filter((d: PersonDocumentRecord) => !d.isCompleted).length} čeká</Badge>
            )}
          </div>
          <table className={styles.docTable}>
            <thead>
              <tr>
                <th className={styles.th}>Dokument</th>
                <th className={styles.th}>Typ</th>
                <th className={styles.th}>Termín</th>
                <th className={styles.th}>Stav</th>
                <th className={styles.th}>Splněno</th>
                {canEdit && <th className={styles.th}></th>}
              </tr>
            </thead>
            <tbody>
              {docs.map((doc: PersonDocumentRecord) => {
                const overdue = !doc.isCompleted && deadlinePassed(doc.deadlineDate)
                return (
                  <tr key={doc.id}>
                    <td className={styles.td} style={{ fontWeight: 600 }}>
                      {doc.documentUrl
                        ? <a href={doc.documentUrl} target="_blank" rel="noopener noreferrer" style={{ color: tokens.colorBrandForeground1 }}>{doc.documentName}</a>
                        : doc.documentName}
                    </td>
                    <td className={styles.td}>
                      <Badge appearance="outline" color={doc.documentType === 1 ? 'warning' : doc.documentType === 3 ? 'success' : 'informative'} size="small">
                        {DocTypeLabels[doc.documentType]}
                      </Badge>
                    </td>
                    <td className={styles.td} style={{ fontSize: 12 }}>{fmt(doc.deadlineDate) ?? '—'}</td>
                    <td className={styles.td}>
                      <Badge color={doc.isCompleted ? 'success' : overdue ? 'danger' : 'informative'}>
                        {doc.isCompleted ? 'Splněno' : overdue ? 'Po termínu' : 'Čeká'}
                      </Badge>
                    </td>
                    <td className={styles.td} style={{ fontSize: 12 }}>{fmt(doc.completedAt) ?? '—'}</td>
                    {canEdit && (
                      <td className={styles.td}>
                        {!doc.isCompleted && doc.documentType !== 3 && (
                          <Button size="small" appearance="subtle" icon={<CheckmarkCircle24Regular />}
                            disabled={completeMutation.isPending}
                            onClick={() => completeMutation.mutate(doc.id)}>
                            Splnit
                          </Button>
                        )}
                        {!doc.isCompleted && doc.documentType === 3 && (
                          <span style={{ fontSize: 12, color: 'var(--colorNeutralForeground3, #888)' }}>Výsledek z LMS</span>
                        )}
                      </td>
                    )}
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
